import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useUser } from "@/app/session.tsx";
import { TopBar } from "@/components/TopBar.tsx";
import { formatCount } from "@/i18n/index.ts";
import { api } from "@/lib/api.ts";
import type { QueryTrendCount } from "@/lib/api-types.ts";

export function TrendRows({ rows, empty }: { rows: QueryTrendCount[]; empty: string }) {
  const { i18n } = useTranslation();
  const largest = Math.max(...rows.map((row) => row.query_count), 1);
  if (rows.length === 0) return <p className="trend-empty">{empty}</p>;
  return (
    <ol className="trend-list">
      {rows.map((row, index) => (
        <li className="trend-row" key={row.name}>
          <span className="trend-rank" aria-hidden="true">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="trend-name">{row.name}</span>
          <span className="trend-count">
            {formatCount(row.query_count, i18n.language)}
            <small>{new Intl.NumberFormat(i18n.language, { style: "percent" }).format(row.share)}</small>
          </span>
          <span className="trend-track" aria-hidden="true">
            <span style={{ width: `${Math.max((row.query_count / largest) * 100, 2)}%` }} />
          </span>
        </li>
      ))}
    </ol>
  );
}

function formatDate(value: string | null, language: string): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(language === "en" ? "en-GB" : "id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

export function QueryTrendsRoute() {
  const { t, i18n } = useTranslation();
  const user = useUser();
  const allowed = user.role === "admin" || user.role === "operator";
  const trends = useQuery({
    queryKey: ["query-trends"],
    queryFn: () => api.queryTrends(12),
    enabled: allowed,
  });

  if (!allowed) {
    return (
      <div className="lib">
        <TopBar />
        <main className="trend-shell">
          <p className="eyebrow">{t("trends.eyebrow")}</p>
          <h1 className="trend-title">{t("trends.forbiddenTitle")}</h1>
          <p className="page-sub">{t("trends.forbiddenBody")}</p>
        </main>
      </div>
    );
  }

  const report = trends.data;
  return (
    <div className="lib">
      <TopBar />
      <main className="trend-shell">
        <header className="trend-mast">
          <div>
            <p className="eyebrow">{t("trends.eyebrow")}</p>
            <h1 className="trend-title">{t("trends.title")}</h1>
            <p className="trend-intro">{t("trends.subtitle")}</p>
          </div>
          {report ? (
            <dl className="trend-pulse" aria-label={t("trends.summary")}>
              <div>
                <dt>{t("trends.tracked")}</dt>
                <dd>{formatCount(report.tracked_queries, i18n.language)}</dd>
              </div>
              <div>
                <dt>{t("trends.classified")}</dt>
                <dd>{formatCount(report.classified_queries, i18n.language)}</dd>
              </div>
              <div>
                <dt>{t("trends.attributed")}</dt>
                <dd>{formatCount(report.source_attributed_queries, i18n.language)}</dd>
              </div>
            </dl>
          ) : null}
        </header>

        {trends.isLoading ? <p className="trend-state">{t("common.loading")}</p> : null}
        {trends.isError ? <p className="trend-state trend-state-error">{t("trends.error")}</p> : null}
        {report ? (
          <>
            {report.tracked_queries === 0 ? (
              <section className="trend-zero">
                <span className="trend-zero-mark" aria-hidden="true">00</span>
                <div>
                  <h2>{t("trends.emptyTitle")}</h2>
                  <p>{t("trends.emptyBody")}</p>
                </div>
              </section>
            ) : (
              <div className="trend-grid">
                <section className="trend-ledger trend-ledger-source">
                  <div className="trend-section-head">
                    <div>
                      <p className="eyebrow">01 / {t("trends.sourceEyebrow")}</p>
                      <h2>{t("trends.sources")}</h2>
                    </div>
                    <span>{t("trends.queryShare")}</span>
                  </div>
                  <TrendRows rows={report.sources} empty={t("trends.noSources")} />
                </section>

                <section className="trend-ledger trend-ledger-category">
                  <div className="trend-section-head">
                    <div>
                      <p className="eyebrow">02 / {t("trends.categoryEyebrow")}</p>
                      <h2>{t("trends.categories")}</h2>
                    </div>
                    {report.unclassified_queries > 0 ? (
                      <span>{t("trends.unclassified", { count: report.unclassified_queries })}</span>
                    ) : null}
                  </div>
                  <TrendRows rows={report.categories} empty={t("trends.noCategories")} />
                </section>
              </div>
            )}

            <section className="trend-keywords">
              <div className="trend-keyword-head">
                <p className="eyebrow">03 / {t("trends.keywordEyebrow")}</p>
                <h2>{t("trends.keywords")}</h2>
              </div>
              {report.keywords.length ? (
                <ol className="keyword-cloud">
                  {report.keywords.map((item, index) => (
                    <li key={item.keyword}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <strong>{item.keyword}</strong>
                      <small>× {formatCount(item.query_count, i18n.language)}</small>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="trend-empty">{t("trends.noKeywords")}</p>
              )}
            </section>

            <footer className="trend-foot">
              <span>{t("trends.forwardOnly")}</span>
              <span>
                {formatDate(report.tracking_started_at, i18n.language)} — {formatDate(report.tracking_through_at, i18n.language)}
              </span>
            </footer>
          </>
        ) : null}
      </main>
    </div>
  );
}
